import React, { useState } from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import {
  primaryLanguage,
  secondaryLanguages,
  unfinishedLanguages,
  languageNames,
} from 'languages/config';
import { ICON } from 'resources/constants';

import {
  ComponentWrapper,
  Popover,
  Button,
  Spacer,
  Wrapper,
  Text,
  Icon,
  List,
  ListItem,
} from './elements';

import Ico16 from 'dc/Ico16';
import Ico20 from 'dc/Ico20';

import { useText } from 'common/hooks';

const languages = [
  primaryLanguage,
  ...secondaryLanguages.filter(l => !unfinishedLanguages.includes(l)),
];

type Props = {
  readonly className?: string;
  readonly popupPosition: string;
};

const deps = {
  language: state.appContainer.language.locale,
  languageSelected: sequences`appContainer.languageSelected`,
};

export function LanguageSelector({
  className,
  popupPosition,
  language,
  languageSelected,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const [expanded, setExpanded] = useState(false);

  function onBlur(e) {
    const { currentTarget } = e;

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        setExpanded(false);
      }
    }, 0);
  }

  return (
    <ComponentWrapper className={className} onBlur={onBlur}>
      <Button
        onClick={() => setExpanded(!expanded)}
        aria-haspopup
        aria-expanded={expanded}
        aria-label={t('menu.language_menu')}
      >
        <Ico20 icon={ICON.language} />
        <Ico16 icon={ICON.arrowDropDown} />
      </Button>
      {
        <Popover $popupPosition={popupPosition} $expanded={expanded}>
          <Spacer />
          <List role={'menubar'} aria-label={t('menu.language_menu')}>
            {languages.map(languageOption => (
              <ListItem key={languageOption} role={'none'}>
                <Button
                  onClick={() => languageSelected({ language: languageOption })}
                >
                  <Wrapper $selected={language === languageOption}>
                    <Text>{languageNames[languageOption]}</Text>
                    <Icon
                      $selected={language === languageOption}
                      icon={ICON.check}
                    />
                  </Wrapper>
                </Button>
              </ListItem>
            ))}
          </List>
          <Spacer />
        </Popover>
      }
    </ComponentWrapper>
  );
}

export default connect<Props, typeof deps>(deps, LanguageSelector);
